import React, { useMemo } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { toast } from 'react-toastify';

import { UtilInfo } from '../../api/models/UtilInfo'; // Adjust path
import { useUserProfile } from 'hooks'; // Adjust path
import styles from './UtilCard.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ALL_ICONS } from '../UtilDialog/UtilDialog';
import DefaultCopyIcon from './defaultCopyIcon.svg';
import HoverCopyIcon from './hoverCopyIcon.svg';
import { getCategoryDisplayName } from 'features/utils/api/models/Category';
import { EnvironmentInfo, EnvironmentType } from 'features/utils/api/models/Environment';
import { sanitizeHtml } from 'features/utils/utils/htmlText';

interface UtilCardProps {
  util: UtilInfo;
  viewMode: 'grid' | 'list';
  onEdit: (util: UtilInfo) => void;
  onDelete: (util: UtilInfo) => void;
  onUsed: (utilId: string) => void;
  onPinToggle: (utilId: string) => void;
  isPinned: boolean;
  activeEnvironmentFilter: EnvironmentType | null;
}

const UtilCard: React.FC<UtilCardProps> = ({
  util,
  viewMode,
  onEdit,
  onDelete,
  onUsed,
  onPinToggle,
  isPinned,
  activeEnvironmentFilter,
}) => {
  const userProfile = useUserProfile();

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (userProfile?.isAdmin) {
      onDelete(util);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (userProfile?.isAdmin) {
      onEdit(util);
    }
  };

  const handlePinToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPinToggle(util.id);
  };

  const handleCopyToClipboard = async (e: React.MouseEvent, urlToCopy: string, envTitle: string) => {
    e.stopPropagation();
    onUsed(util.id);
    try {
      await navigator.clipboard.writeText(urlToCopy);
      toast.success(`URL for "${envTitle}" copied!`);
    } catch (err) {
      console.error('Failed to copy URL: ', err);
      toast.error('Failed to copy URL.');
    }
  };

  const handleOpenUrl = (e: React.MouseEvent, urlToOpen: string) => {
    e.stopPropagation();
    onUsed(util.id);
    window.open(urlToOpen, '_blank');
  };

  const environmentsToDisplay = useMemo(() => {
    if (activeEnvironmentFilter != null && util.environments !== null && util.environments !== undefined) {
      return util.environments.filter((env) => env.environmentType === activeEnvironmentFilter);
    }
    return util.environments;
  }, [util.environments, activeEnvironmentFilter]);

  const getIcon = (iconName: string) => {
    return ALL_ICONS.find((icon) => icon.name === iconName) ?? ALL_ICONS[0];
  };

  const renderCardIcon = (iconName: string) => {
    const iconItem = getIcon(iconName);
    if (!iconItem) return null;

    if (iconItem.type === 'svg' || iconItem.type === 'png') {
      const icon = iconItem.renderData as string;
      return <img key={iconItem.name} src={icon} alt={iconItem.name} className={styles.iconStyle} />;
    } else if (iconItem.type === 'fontawesome') {
      const faIcon = iconItem.renderData as IconDefinition;
      return <FontAwesomeIcon icon={faIcon} className={styles.iconStyle} />;
    }
    return null;
  };

  const renderDescription = () => {
    if (!util.description) {
      return <p className={styles.description}>No description available.</p>;
    }
    return <div className={styles.description} dangerouslySetInnerHTML={{ __html: sanitizeHtml(util.description) }} />;
  };

  const renderEnvironmentButtons = (environments: EnvironmentInfo[]) => {
    if (!environmentsToDisplay || environmentsToDisplay.length === 0) {
      return null;
    }

    return (
      <div className={styles.environmentsWrapper}>
        <div className={styles.environmentsList}>
          {environmentsToDisplay
            .sort((a, b) => a.order - b.order)
            .map((env, index) => (
              <div key={env.id ?? index} className={styles.environmentButtonGroup}>
                <Button
                  themeColor='primary'
                  fillMode='outline'
                  size='large'
                  className={styles.envOpenButton}
                  onClick={(e) => handleOpenUrl(e, env.url)}
                  title={`Open ${env.title} (${env.url})`}
                >
                  {env.title}
                </Button>
                <Button
                  themeColor='primary'
                  fillMode='outline'
                  size='large'
                  className={styles.envCopyButton}
                  onClick={(e) => handleCopyToClipboard(e, env.url, env.title)}
                  aria-label={`Copy URL for ${env.title}`}
                  title={`Copy URL for ${env.title}`}
                >
                  <img src={DefaultCopyIcon} alt='' className={`${styles.copyIconSvg} ${styles.defaultCopyIcon}`} />
                  <img src={HoverCopyIcon} alt='' className={`${styles.copyIconSvg} ${styles.hoverCopyIcon}`} />
                </Button>
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`${styles.serviceCard} ${viewMode === 'list' ? styles.listView : ''}`}>
      {userProfile?.isAdmin && (
        <div className={styles.actionButtonsContainer}>
          <Button
            icon={isPinned ? 'pin' : 'unpin'}
            fillMode='flat'
            themeColor='primary'
            className={styles.actionButton}
            onClick={handlePinToggle}
            title={isPinned ? 'Unpin App' : 'Pin App'}
            aria-label={isPinned ? `Unpin ${util.title}` : `Pin ${util.title}`}
          />
          <Button
            icon='edit'
            fillMode='flat'
            themeColor='primary'
            className={styles.actionButton}
            onClick={handleEditClick}
            title='Edit App'
            aria-label={`Edit ${util.title}`}
          />
          <Button
            icon='trash'
            fillMode='flat'
            themeColor='error'
            className={styles.actionButton}
            onClick={handleDeleteClick}
            title='Delete App'
            aria-label={`Delete ${util.title}`}
          />
        </div>
      )}

      <div className={styles.cardContentWrapper}>
        <div className={styles.cardHeaderSection}>{renderCardIcon(util.icon)}</div>
        <div className={styles.mainContentSection}>
          <span className={styles.title}>{util.title}</span>
          <span className={styles.categoryLabel}>{getCategoryDisplayName(util.category)}</span>
          {viewMode === 'list' ? (
            <div className={styles.contentRow}>
              {renderDescription()}
              {renderEnvironmentButtons(util.environments)}
            </div>
          ) : (
            <>
              {renderDescription()}
              {renderEnvironmentButtons(util.environments)}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UtilCard;
